export const nativeContent = {
  humanPublishNote:
    'The engine only drafts. A person reviews, edits and publishes with their own account.',
  channels: {
    reddit: {
      description: 'A useful reply for an existing thread, or a standalone post, ready to publish.',
    },
    x: { description: 'Post variants or a thread, with reply angles for the first minutes.' },
    linkedin: { description: 'A professional post in your voice.' },
    instagram: { description: 'Visual-first captions.' },
    quora: { description: 'Answers that position your expertise.' },
    medium: { description: 'Long-form articles to build authority.' },
  },
  formats: {
    contribution: 'Contribution (reply)',
    post: 'Post',
    xPost: 'Post',
    thread: 'Thread',
  },
  status: {
    pending: 'Pending',
    generating: 'Generating',
    completed: 'Completed',
    failed: 'Failed',
  },
  sourceType: {
    plan_item: 'Content plan',
    scratch: 'From scratch',
    knowledge: 'Meeting or notes',
  },
  state: {
    draft: 'Draft',
    approved: 'Approved',
    scheduled: 'Scheduled',
    scheduleFailed: 'Publish failed',
    published: 'Published',
    scheduledTooltip: 'Publishes on {{datetime}}',
    scheduleFailedTooltip: 'The scheduled publish failed: {{reason}}',
    scheduleFailedTooltipNoReason: 'The scheduled publish failed. Open the piece for details.',
  },
  qa: {
    unavailable: 'Not checked',
    offVoice: 'Off voice',
    showReport: 'View quality check',
    hideReport: 'Hide quality check',
    failedRules: 'Failed checks',
    suggestedFixes: 'Suggested fixes',
    verdict: {
      pass: 'Quality OK',
      needsChanges: 'Minor issues',
      fail: 'Quality issues',
    },
    ruleFallback: 'Style check',
    rules: {
      'rd-answers-thread': 'Directly answers what the thread is asking',
      'rd-grounded': 'Every fact is real (nothing invented)',
      'rd-brand-natural': 'Brand is mentioned at most once, naturally',
      'rd-no-features': 'No feature list or salesy tone',
      'rd-no-cta': 'No calls to action or links that were not provided',
      'rd-peer-tone': 'Reads like a peer, not a corporate voice',
      'rd-length': 'Length between 120 and 300 words',
      'rd-no-disclosure': "Doesn't disclose working at the company",
      'x-le-280': 'Each post/reply is 280 characters or fewer',
      'x-hook-delivers': 'The hook delivers on its promise',
      'x-grounded': 'Every fact is real (nothing invented)',
      'x-no-hashtags': 'No hashtags',
      'x-no-link-in-body': 'No link inside the post body (only in a reply)',
      'x-reply-angles': 'Includes 2-3 useful reply angles',
      'x-thread-shape': 'Thread has 5-7 tweets and a concrete close',
      'x-voice': 'Tone matches the brand voice',
      'li-grounded': 'Every fact is real (nothing invented)',
      'li-hook-strong': 'The hook is strong and the post delivers on it',
      'li-stance-developed': 'Develops a real point of view, not a summary',
      'li-no-emdash': 'Avoid the em-dash (—)',
      'li-no-x-sino-y': 'Avoid the "not X, but Y" formula',
      'li-no-rule-of-three': 'Avoid rhetorical triads ("X, Y and Z")',
      'li-no-cta-generic': 'No generic closing question — ends with a concrete next step',
      'li-no-overformat': 'No excess bold, emoji or bullet points',
      'li-length': 'Length between 600 and 1400 characters',
      'li-paragraphs': 'Short paragraphs, 1-2 lines',
      'li-voice-person': 'Written in first person (when personal voice is chosen)',
    },
  },
  wizard: {
    back: 'Back',
    continue: 'Continue',
    generate: 'Generate draft',
    viewList: 'View all content',
    channelStep: {
      title: 'What do you want to create?',
      subtitle: 'Pick a channel. The engine drafts native content for each one.',
      comingSoon: 'Coming soon',
      humanBadge: 'A human publishes — the engine only drafts',
      publishesFromReferentBadge: 'Publish from Referent with your account',
    },
    sourceStep: {
      title: 'Where does the raw material come from?',
      subtitle: 'The engine adds the rest: brand voice and knowledge.',
      scratch: {
        title: 'From scratch',
        description: 'You bring the raw idea and the real datum.',
      },
      knowledge: {
        title: 'From a meeting or your notes',
        description: 'Paste meeting notes, a brief, or any raw material. The engine uses it as the base.',
      },
      notesLabel: 'Paste your material',
      notesPlaceholder: 'Meeting notes, key facts, quotes…',
      notesHelper: 'Minimum {{min}} characters — {{count}} so far.',
      kbLabel: 'Anchor on knowledge base (optional, up to {{max}})',
      kbSearchPlaceholder: 'Search the knowledge base…',
      kbNoResults: 'No results.',
    },
    formStep: {
      title: 'Create for {{channel}}',
      subtitle: 'Give the engine what only you have. It handles the rest.',
      brandVoiceLabel: 'Brand voice',
    },
    inference: {
      title: 'What the engine infers',
      brandVoice: 'Brand voice',
      noBrandVoice: 'No specific voice',
      kbSources: 'Knowledge sources',
      kbEmpty: 'None selected.',
      planRow: 'Plan row',
      keyword: 'Keyword',
      planTitle: 'Title',
      approach: 'Approach',
      footer: 'You bring the irreplaceable. The engine brings the rest.',
    },
    generating: {
      title: 'Generating your content',
      hint: 'This takes a few seconds — keep this screen open.',
      drafting: 'Drafting…',
      qa: 'Running the quality check…',
    },
  },
  forms: {
    reddit: {
      subreddit: 'Subreddit',
      subredditPlaceholder: 'r/subreddit',
      topic: 'Topic',
      differentiator: 'What makes this post worth it (the angle only you have)',
      differentiatorHelper: 'Something verifiable — a real case, a number, a lesson learned.',
      titleOptional: 'Title (optional)',
      threadUrl: 'Thread URL',
      evidence: 'The real datum/case that anchors your reply',
      evidenceHelper:
        'Something verifiable: a number, a case, a date — what the AI cannot invent.',
      stance: 'Your stance in one sentence',
      subredditContext: 'Subreddit notes (optional)',
    },
    x: {
      realDatum: 'The REAL result/deadline/mistake',
      realDatumHelper: 'The irreplaceable hook material — what the AI cannot invent.',
      hookSeed: 'Your own hook (optional)',
      linkUrl: 'Link (optional)',
      linkUrlHelper: 'Goes in the first reply, never in the tweet body.',
      voice: 'Voice',
      voicePersonal: 'Personal',
      voiceBrand: 'Brand',
    },
    linkedin: {
      stance: 'Your stance',
      realDatum: 'The real, verifiable datum/anecdote',
      realDatumHelper: 'Something checkable — a real number, name or date the AI cannot invent.',
      hookType: 'Hook type',
      hookTypeContrarian: 'Contrarian',
      hookTypeContrarianHint: 'Challenge a common belief in your industry.',
      hookTypeStat: 'Data point',
      hookTypeStatHint: 'Open with a surprising number or stat.',
      hookTypeStory: 'Story',
      hookTypeStoryHint: 'Start with a real anecdote or scene.',
      hookTypeDirect: 'Direct',
      hookTypeDirectHint: 'Straight to the point, no preamble.',
      closingNextStep: 'Closing / next step (optional)',
      closingNextStepHelper: 'A concrete next step. Left blank, the engine proposes one — never a generic CTA question.',
      voice: 'Voice',
      voicePersonal: 'Personal brand',
      voiceBrand: 'Company page',
    },
    errors: {
      required: 'Please fill in this field.',
      url: 'Enter a valid URL (https://…).',
      tooShort: 'Write a little more.',
      tooLong: 'This is too long.',
      selectOption: 'Choose an option.',
    },
  },
  personalVoice: {
    voiceLabel: 'Voice',
    myVoice: 'Your personal voices',
    back: 'Back to the engine',
    hintLoadFailed: "We couldn't load your personal voices.",
    listTitle: 'Your personal voices',
    listSubtitle: 'Named voices the engine writes in when you sign a piece as personal.',
    newVoice: 'New voice',
    editVoice: 'Edit voice',
    nameLabel: 'Voice name',
    namePlaceholder: 'E.g.: My LinkedIn voice',
    description:
      'Paste your 3-5 best real posts: the engine uses them as examples to write with your tone, rhythm and vocabulary.',
    channelsLabel: 'Channels',
    channelsHelper: 'Which channels this voice applies to. Each channel uses its own register.',
    postsLabel: 'Your best posts',
    postLabel: 'Post {{number}}',
    postPlaceholder: 'Paste one of your real posts here, exactly as you published it…',
    addPost: 'Add another post',
    removePost: 'Remove this post',
    positioningLabel: 'Positioning (optional)',
    positioningPlaceholder: 'E.g.: Technical founder writing about B2B product without fluff',
    dosLabel: 'Do (optional, one rule per line)',
    dontsLabel: "Don't (optional, one rule per line)",
    save: 'Save voice',
    cancel: 'Cancel',
    columnName: 'Name',
    columnChannels: 'Channels',
    columnPosts: 'Posts',
    columnActions: 'Actions',
    empty: {
      title: 'No personal voices yet',
      description:
        'Create a voice from your real posts so the engine can write LinkedIn or X content in your own tone.',
      cta: 'Create your first voice',
    },
    deleteConfirmTitle: 'Delete this voice?',
    deleteConfirm: 'This cannot be undone.',
    deleteAction: 'Delete',
    selectLabel: 'Personal voice',
    selectNone: 'No personal voice',
    selectManage: 'Manage voices',
    selectEmptyForChannel: 'You have no voices for this channel.',
    toast: {
      created: 'Personal voice created.',
      updated: 'Personal voice updated.',
      deleted: 'Personal voice deleted.',
      saveFailed: 'Could not save your personal voice.',
      deleteFailed: 'Could not delete the voice.',
    },
    errors: {
      postTooShort: 'Each post needs at least 50 characters.',
      postTooLong: 'Each post can be up to 3000 characters.',
      listItemTooLong: 'Each rule can be up to {{max}} characters.',
      tooManyListItems: 'You can add up to {{max}} rules per list.',
    },
  },
  result: {
    copy: 'Copy',
    copyAll: 'Copy all',
    pieceActions: 'Piece actions',
    export: 'Export',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    reddit: {
      threadContext: 'Destination thread',
    },
    x: {
      variants: 'Post variants',
      thread: 'Thread',
      variantNumber: 'Variant {{number}}',
      tweetNumber: 'Tweet {{current}}/{{total}}',
      repliesTitle: 'Your replies for the first 30 minutes',
      repliesNote: 'Replying fast multiplies reach.',
      linkReply: 'Link reply',
    },
    regenerate: {
      action: 'Regenerate',
      title: 'Regenerate draft',
      description: 'Optionally add feedback to steer the new draft. The piece goes back to Draft.',
      placeholder: 'e.g. make it shorter, more concrete, less salesy…',
      cancel: 'Cancel',
      confirm: 'Regenerate',
      regenerating: 'Regenerating…',
      regeneratingHint: 'This can take up to a minute — the draft will update here automatically.',
    },
    publishedLocked: 'This piece was already published.',
    approve: {
      action: 'Approve',
    },
    unapprove: {
      action: 'Back to draft',
    },
    publish: {
      action: 'Mark as published',
      title: 'Mark as published',
      description:
        'Record where and when this piece was published. This only leaves the record — nothing goes out from here.',
      urlLabel: 'Publication URL (optional)',
      urlPlaceholder: 'https://…',
      dateLabel: 'Publication date',
      cancel: 'Cancel',
      confirm: 'Record publication',
    },
    composer: {
      blockedNotApproved: 'Approve the piece to publish it. You can keep writing and saving.',
      blockedAlreadyPublished: 'This piece is already recorded as published.',
    },
    versions: {
      action: 'History',
      generationAction: 'Generation history',
      title: 'Version history',
      description:
        'Every overwrite of this piece — regenerations, manual saves and restores — newest first.',
      empty: 'No versions yet. They appear when the piece is regenerated or edited.',
      version: 'v{{version}}',
      back: 'Back to the list',
      unit: 'Unit {{position}}',
      trigger: {
        regenerate: 'Regeneration',
        manual_edit: 'Manual edit',
        restore: 'Restore',
      },
      time: {
        justNow: 'Just now',
        minutesAgo: '{{minutes}}m ago',
        hoursAgo: '{{hours}}h ago',
        daysAgo: '{{days}}d ago',
      },
      restore: {
        action: 'Restore this version',
        title: 'Restore v{{version}}?',
        body:
          'The current text is saved as a new version first, so nothing is lost. The approval is cleared — the restored text needs its own sign-off.',
        confirm: 'Restore',
        cancel: 'Cancel',
        blocked: 'A published piece cannot be restored.',
      },
      retry: 'Retry',
      toast: {
        restored: 'Version restored',
      },
      errors: {
        loadFailed: 'Could not load the versions.',
        detailLoadFailed: 'Could not load this version.',
        restoreFailed: 'Could not restore the version.',
      },
    },
    toast: {
      copied: 'Copied to clipboard',
      saved: 'Changes saved',
      approved: 'Piece approved',
      unapproved: 'Piece moved back to draft',
      published: 'Publication recorded',
    },
    errors: {
      generateFailed: 'Generation failed. Please try again.',
      regenerateFailed: 'Regeneration failed. Please try again.',
      saveFailed: 'Could not save changes.',
      approveFailed: 'Could not approve the piece.',
      unapproveFailed: 'Could not move back to draft.',
      publishFailed: 'Could not record the publication.',
    },
  },
  create: {
    backToList: 'Back to content',
  },
  detail: {
    notFound: 'This piece was not found.',
  },
  list: {
    title: 'Content Engine',
    subtitle: 'Every native piece drafted by the engine, by channel.',
    newAction: 'Create content',
    searchPlaceholder: 'Search…',
    retry: 'Retry',
    filters: {
      channel: 'Channel',
      allChannels: 'All channels',
      state: 'State',
      allStates: 'All states',
      quality: 'Quality',
      allQualities: 'All qualities',
      source: 'Source',
      allSources: 'All sources',
    },
    columns: {
      piece: 'Piece',
      channel: 'Channel',
      format: 'Format',
      source: 'Source',
      state: 'State',
      actions: 'Actions',
    },
    empty: {
      title: 'No content yet',
      description: 'Create your first native piece for Reddit, X or LinkedIn.',
      cta: 'Create content',
    },
    delete: {
      title: 'Delete this piece?',
      description: 'This cannot be undone.',
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    toast: {
      deleted: 'Piece deleted',
    },
    pagination: {
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {{page}} of {{total}}',
    },
    errors: {
      loadFailed: 'Could not load content.',
      deleteFailed: 'Could not delete the piece.',
    },
  },
  copilot: {
    title: 'Refy',
    subtitle: 'Ask for hooks, angles or rewrites — grounded in your brand voice.',
    writingWithVoice: 'with your voice: {{voice}}',
    open: 'Refy',
    close: 'Close',
    inputPlaceholder: 'Ask Refy…',
    send: 'Send',
    thinking: 'Refy is typing…',
    welcome:
      "Hi! I'm Refy. Tell me your idea for your {{channel}} post and I'll draft it — or ask me for hooks, a sharper angle, or to fill the form from your notes.",
    suggestionFor: 'Suggestion for {{field}}',
    apply: 'Apply',
    applied: 'Applied to {{field}}',
    unknownField: 'This suggestion has no matching field — copy it manually.',
    fieldFallback: 'the field',
    fillFormTitle: '{{count}} fields to fill',
    fillFormApply: 'Click to fill the form',
    fillApplied: 'Filled {{count}} fields',
    fields: {
      stance: 'Stance',
      realDatum: 'Data point',
      hookType: 'Hook type',
      closingNextStep: 'Closing',
      hookSeed: 'Hook',
      linkUrl: 'Link',
      voiceOwner: 'Voice',
      threadUrl: 'Thread',
      evidence: 'Evidence',
      subredditContext: 'Subreddit context',
      subreddit: 'Subreddit',
      topic: 'Topic',
      differentiator: 'Differentiator',
      title: 'Title',
    },
    quickPrompts: {
      fillForm: 'Fill the form',
      anotherHook: 'Give me another hook',
      sharpen: 'Make it sharper',
      improveClosing: 'Improve the closing',
      moreConcrete: 'Make it more concrete',
    },
    errors: {
      generic: 'Refy could not respond. Try again.',
    },
  },
} as const
